import React from 'react';
import { DollarSign, TrendingUp, TrendingDown, Calculator, Image as ImageIcon, Package, Truck, MapPin } from 'lucide-react';

function FinancialSummary({ financials, paymentImages = [], onShowImages }) {
  const {
    totalCollected,
    totalToDeposit,
    totalDeliveryCount,
    deliveryCount,
    deliveryCountProvince,
    pricePerDelivery,
    totalServiceFee,
    totalExchange,
    totalProvinciaCosts,
    dropshipDeliveries,
    dropshipTotalCost,
    // New breakdown fields
    limaCollected,
    limaServiceFee,
    limaDropshipCost,
    limaToDeposit,
    provinciaCollected,
    provinciaServiceFee,
    provinciaTransportCost,
    provinciaDropshipCost,
    provinciaToDeposit,
  } = financials;

  const hasImages = paymentImages && paymentImages.length > 0;

  return (
    <div className="space-y-4">
      {/* Main Header Card */}
      <div className="bg-gradient-to-br from-[#B1336E] to-[#E6B43C] rounded-2xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <button 
              type="button"
              onClick={() => {
                if (hasImages && onShowImages) {
                  onShowImages();
                }
              }}
              disabled={!hasImages}
              className={`w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center relative group transition-all ${
                hasImages 
                  ? 'cursor-pointer hover:bg-white/30 hover:scale-110' 
                  : 'cursor-default opacity-50'
              }`}
            >
              <DollarSign className="w-6 h-6" />
              {hasImages && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <span className="text-xs text-white font-bold font-montserrat">
                    {paymentImages.length}
                  </span>
                </div>
              )}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg font-montserrat shadow-lg w-64 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {hasImages 
                  ? `Ver ${paymentImages.length} comprobante${paymentImages.length !== 1 ? 's' : ''} de depósito` 
                  : 'No hay comprobantes disponibles para esta fecha'}
              </span>
            </button>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-extrabold font-montserrat">Resumen Financiero</h2>
              {hasImages && (
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {paymentImages.length}
                </span>
              )}
            </div>
            <p className="text-sm text-white/80 font-montserrat">Análisis del día seleccionado</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Collected */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" />
              <p className="text-xs font-medium font-montserrat">Total Recaudado</p>
            </div>
            <p className="text-2xl font-extrabold font-montserrat">
              S/ {totalCollected.toFixed(2)}
            </p>
            <p className="text-xs text-white/70 mt-1 font-montserrat">
              {totalExchange > 0 
                ? `Cobrado + Transferencias - Cambio (S/ ${totalExchange.toFixed(2)})`
                : 'Cobrado + Transferencias'}
            </p>
          </div>

          {/* Service Fee + Transport + Dropship */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="w-4 h-4" />
              <p className="text-xs font-medium font-montserrat">Costos Totales</p>
            </div>
            <p className="text-2xl font-extrabold font-montserrat">
              S/ {(totalServiceFee + totalProvinciaCosts + dropshipTotalCost).toFixed(2)}
            </p>
            <div className="text-xs text-white/70 mt-1 font-montserrat space-y-0.5">
              <div>Servicio: S/ {totalServiceFee.toFixed(2)}</div>
              {totalProvinciaCosts > 0 && (
                <div>Transporte: S/ {totalProvinciaCosts.toFixed(2)}</div>
              )}
              {dropshipTotalCost > 0 && (
                <div>Dropship: S/ {dropshipTotalCost.toFixed(2)}</div>
              )}
            </div>
          </div>

          {/* Total to Deposit */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4" />
              <p className="text-xs font-medium font-montserrat">A Depositar Total</p>
            </div>
            <p className="text-2xl font-extrabold font-montserrat">
              S/ {totalToDeposit.toFixed(2)}
            </p>
            <p className="text-xs text-white/70 mt-1 font-montserrat">
              Recaudado - Todos los Costos
            </p>
          </div>

          {/* Deliveries Count */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4" />
              <p className="text-xs font-medium font-montserrat">Entregas Cobradas</p>
            </div>
            <p className="text-2xl font-extrabold font-montserrat">
              {totalDeliveryCount}
            </p>
            <div className="text-xs text-white/70 mt-1 font-montserrat space-y-0.5">
              {deliveryCount > 0 && <div>Lima: {deliveryCount}</div>}
              {deliveryCountProvince > 0 && <div>Provincia: {deliveryCountProvince}</div>}
              {dropshipDeliveries > 0 && <div>Dropship: {dropshipDeliveries}</div>}
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-xs text-white/80 font-montserrat">
            <strong>Nota:</strong> Solo se contabilizan entregas con estado "Entregado" o "Rechazado". 
            {totalProvinciaCosts > 0 && ' Los costos de transporte provincia están incluidos.'}
            {dropshipTotalCost > 0 && ' Los costos de dropshipping están descontados.'}
          </p>
        </div>
      </div>

      {/* Lima and Provincia Breakdown Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* LIMA CARD - Same Day Settlement */}
        {deliveryCount > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-[#B1336E]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#B1336E]/10 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-[#B1336E]" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#B1336E] font-montserrat">
                  Entregas Lima
                </h3>
              </div>
            </div>

            <div className="space-y-3">
              {/* Lima Collected */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#67A43C]" />
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Recaudado
                  </span>
                </div>
                <span className="text-lg font-bold text-slate-900 font-montserrat">
                  S/ {limaCollected.toFixed(2)}
                </span>
              </div>

              {/* Lima Service Fee */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Servicio Fast Max
                  </span>
                </div>
                <span className="text-lg font-bold text-red-600 font-montserrat">
                  - S/ {limaServiceFee.toFixed(2)}
                </span>
              </div>

              {/* Lima Dropship Cost */}
              {limaDropshipCost > 0 && (
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-slate-700 font-montserrat">
                      Costo Dropship
                    </span>
                  </div>
                  <span className="text-lg font-bold text-red-600 font-montserrat">
                    - S/ {limaDropshipCost.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Lima To Deposit */}
              <div className="bg-[#B1336E]/5 rounded-lg p-3 mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#B1336E]" />
                    <span className="text-sm font-extrabold text-[#B1336E] font-montserrat">
                      A Depositar Hoy
                    </span>
                  </div>
                  <span className="text-2xl font-extrabold text-[#B1336E] font-montserrat">
                    S/ {limaToDeposit.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 font-montserrat ml-7">
                  {deliveryCount} entrega{deliveryCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PROVINCIA CARD - Pending Settlement */}
        {deliveryCountProvince > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-[#E6B43C]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#E6B43C]/10 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-[#E6B43C]" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#E6B43C] font-montserrat">
                  Entregas Provincia
                </h3>
              </div>
            </div>

            <div className="space-y-3">
              {/* Provincia Collected */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#67A43C]" />
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Recaudado
                  </span>
                </div>
                <span className="text-lg font-bold text-slate-900 font-montserrat">
                  S/ {provinciaCollected.toFixed(2)}
                </span>
              </div>

              {/* Provincia Service Fee */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Servicio Fast Max
                  </span>
                </div>
                <span className="text-lg font-bold text-red-600 font-montserrat">
                  - S/ {provinciaServiceFee.toFixed(2)}
                </span>
              </div>

              {/* Provincia Transport Cost */}
              {provinciaTransportCost > 0 && (
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-slate-700 font-montserrat">
                      Costo Transporte
                    </span>
                  </div>
                  <span className="text-lg font-bold text-red-600 font-montserrat">
                    - S/ {provinciaTransportCost.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Provincia Dropship Cost */}
              {provinciaDropshipCost > 0 && (
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-slate-700 font-montserrat">
                      Costo Dropship
                    </span>
                  </div>
                  <span className="text-lg font-bold text-red-600 font-montserrat">
                    - S/ {provinciaDropshipCost.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Provincia To Deposit */}
              <div className="bg-[#E6B43C]/5 rounded-lg p-3 mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#E6B43C]" />
                    <span className="text-sm font-extrabold text-[#E6B43C] font-montserrat">
                      A Depositar (Pendiente)
                    </span>
                  </div>
                  <span className="text-2xl font-extrabold text-[#E6B43C] font-montserrat">
                    S/ {provinciaToDeposit.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 font-montserrat ml-7">
                  {deliveryCountProvince} entrega{deliveryCountProvince !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FinancialSummary;